import { captureException, withScope } from "@sentry/browser"
import { init } from "filestack-js"
import { useEffect, useMemo, useState } from "react"
import { FILESTACK_API_KEY, FILESTACK_STYLES_URL } from "./constants"
// Import Filestack styles - required for picker and preview UI
import "./filestack-styles.css"
import type {
  PDFInfo,
  PickerFileMetadata,
  PickerOptions,
  PickerResponse,
  TransformOptions
} from "./types"
import { getFilestackHandle, isHeic } from "./utils"

/**
 * Initialize Filestack client
 * This replaces the old loadFilestack() script tag loading approach
 */
const getFilestackClient = () => {
  const client = init(FILESTACK_API_KEY, {
    // This will cache the session in the browser's local storage
    // Useful for SSO uploaders like Google, Facebook, etc.
    sessionCache: true
  })

  return client
}

/**
 * Transform a Filestack URL or handle
 * Url can be substituted with a file handle (ex: j1ECLHtTbevSEtIwObMe)
 * Not currently in use
 */
export const transform = async (
  url: string,
  options: TransformOptions
): Promise<string | void> => {
  const client = getFilestackClient()
  let transformURL
  try {
    transformURL = client.transform(url, options)
    return transformURL
  } catch (error) {
    return console.log({ error })
  }
}

/**
 * Open Filestack file picker
 * Includes special handling for HEIC files
 */
export const pick = async (options: PickerOptions): Promise<PickerResponse> => {
  const client = getFilestackClient()
  const filestackResult = await new Promise<PickerResponse>((resolve) => {
    client
      .picker({
        ...options,
        onUploadDone: resolve,
        onFileUploadFailed: (file: PickerFileMetadata, error: Error) => {
          const normalizedError =
            error instanceof Error
              ? error
              : new Error(
                  (error as any)?.message ||
                    `Filestack upload failed: ${
                      (error as any)?.name ||
                      (error as any)?.status ||
                      "Unknown error"
                    }`,
                  { cause: error }
                )

          withScope((scope) => {
            scope.setExtra("file", file)
            if (!(error instanceof Error)) {
              scope.setExtra("originalError", error)
            }
            captureException(normalizedError)
          })
          console.error(
            `Error uploading filestack file(s): ${normalizedError?.message}\n${normalizedError?.stack}\nfile: ${file?.filename}`
          )
        }
      })
      .open()
  })

  // HEIC files: convert and store PNGs so all future operations use the PNG
  // handle rather than the unsupported source handle.
  filestackResult.filesUploaded = await Promise.all(
    filestackResult.filesUploaded.map(async (file) => {
      if (
        file.originalFile &&
        "type" in file.originalFile &&
        isHeic(file.originalFile.type)
      ) {
        try {
          const processURL = `https://cdn.filestackcontent.com/${FILESTACK_API_KEY}/output=format:png,quality:70/${file.handle}`
          const stored = await fetch(
            `https://cdn.filestackcontent.com/${FILESTACK_API_KEY}/store/${processURL}`
          )
          const {
            url,
            type,
            size
          }: { url?: string; type?: string; size?: number } =
            await stored.json()
          const handle = url && getFilestackHandle(url)

          if (!url || !handle) {
            throw new Error("Filestack did not return a stored PNG URL")
          }

          return {
            ...file,
            handle,
            url,
            mimetype: type || "image/png",
            filename: file.filename.replace(/\.(heic|heif)$/i, ".png"),
            size: size || file.size
          }
        } catch (error) {
          console.error("Error processing HEIC file: ", file, error)
        }
      }

      return file
    })
  )

  return filestackResult
}

/**
 * Open Filestack crop tool
 */
export const crop = async (
  urls: string[],
  options: PickerOptions = {}
): Promise<PickerResponse> => {
  const client = getFilestackClient()
  return new Promise((resolve) => {
    client
      .picker({
        ...options,
        onUploadDone: resolve
      })
      .crop(urls)
  })
}

/**
 * Preview a Filestack file in a DOM element
 */
export const preview = async (
  handle: string,
  domID: string,
  cssURL: string = FILESTACK_STYLES_URL
): Promise<void> => {
  const client = getFilestackClient()
  try {
    client.preview(handle, { id: domID, css: cssURL })
  } catch (error) {
    return console.log(
      `Couldn't preview the Filestack file(s) ${handle} in the ${domID} id.`,
      { error }
    )
  }
}

/**
 * Get preview URL used by client.preview
 *
 * Some hackery for getting the preview URL used by client.preview.
 * We override the window.open function called by client.preview
 * and instead store the url, returning it from getPreviewUrl.
 */
export const getPreviewUrl = async (
  handle: string,
  cssURL: string = FILESTACK_STYLES_URL
): Promise<string | URL | undefined> => {
  const client = getFilestackClient()

  // We'll store the preview url.
  let previewUrl: string | URL | undefined
  // Store reference to original window.open.
  const originalOpen = window.open
  // Override, setting url to our previewUrl.
  window.open = (url) => {
    previewUrl = url
    return window
  }
  // Actually call client.preview which calls window.open.
  client.preview(
    handle,
    // @ts-ignore: PickerOptions type expects id prop, their documentation and execution
    // don't require it: "Omit this to open the preview in a new tab".
    {
      css: cssURL
    }
  )
  // Restore original window.open.
  window.open = originalOpen
  // Return the preview url.
  return previewUrl
}

export type PdfInfoResult = {
  data: PDFInfo | null
  error: Error | null
  loading: boolean
}

const emptyPdfInfoResult: PdfInfoResult = {
  data: null,
  error: null,
  loading: false
}

/**
 * Read a Filestack pdfinfo response. Failures are plain text, including
 * "unsupported mimetype" when the stored file is not a PDF.
 */
export const readPdfInfoResponse = async (
  response: Response
): Promise<PDFInfo> => {
  if (!response.ok) {
    const message = (await response.text()).trim()
    throw new Error(message || `PDF info request failed (${response.status})`)
  }

  return response.json()
}

/**
 * React hook for fetching PDF info from Filestack, including request failures.
 */
export const usePdfInfoResult = (pdfUrl: string): PdfInfoResult => {
  const [result, setResult] = useState<PdfInfoResult>(emptyPdfInfoResult)

  useEffect(() => {
    if (!pdfUrl) {
      setResult(emptyPdfInfoResult)
      return
    }

    const handle = getFilestackHandle(pdfUrl)
    if (!handle) {
      setResult({
        data: null,
        error: new Error("Could not read the PDF file."),
        loading: false
      })
      return
    }

    let cancelled = false
    setResult({ data: null, error: null, loading: true })

    fetch(`https://cdn.filestackcontent.com/pdfinfo/${handle}`)
      .then((response) => readPdfInfoResponse(response))
      .then((pdfInfo) => {
        if (!cancelled) {
          setResult({ data: pdfInfo, error: null, loading: false })
        }
      })
      .catch((error) => {
        console.error("Error fetching PDF info:", error)
        if (!cancelled) {
          setResult({
            data: null,
            error: error instanceof Error ? error : new Error(String(error)),
            loading: false
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [pdfUrl])

  return result
}

/**
 * React hook for fetching PDF info from Filestack
 */
export const usePdfInfo = (pdfUrl: string): PDFInfo | null =>
  usePdfInfoResult(pdfUrl).data

/**
 * React hook for extracting dimensions from PDF info
 */
export const usePdfDimensions = (
  pdfUrl: string
): { height: number; width: number } | null => {
  const pdfInfo = usePdfInfo(pdfUrl)

  const dimensions = useMemo(
    () =>
      pdfInfo
        ? {
            height: pdfInfo?.pagesize?.height ?? 0,
            width: pdfInfo?.pagesize?.width ?? 0
          }
        : null,
    [pdfInfo]
  )

  return dimensions
}

/**
 * SVG content for the Filestack icon (inlined for reliability)
 * This is the inactive/grey version that we'll colorize
 */
const FILESTACK_ICON_SVG = `<svg height="58" viewBox="0 0 44 58" width="44" xmlns="http://www.w3.org/2000/svg"><path d="m22 32v-6h-2v6h-6v2h6v6h2v-6h6v-2zm12.997-32h-31.99a2.998 2.998 0 0 0 -3.007 2.996v52.008a3.006 3.006 0 0 0 3.007 2.996h37.986a2.998 2.998 0 0 0 3.007-2.996v-46.006 6.002l-15-15zm-5.997 5 10 10h-9.005c-.55 0-.995-.456-.995-.995z" fill="#bdbdbd" fill-rule="evenodd"/></svg>`

/**
 * Generate a CSS url() value for the brand-colored Filestack icon
 * Used to set the --filestack-brand-icon CSS variable
 *
 * @param brandColor - The brand color to use (hex format, e.g., "#0d71fb")
 * @returns CSS url() value with the brand-colored SVG as a data URI
 */
export const getFilestackBrandIconUrl = (brandColor: string): string => {
  // Replace the fill color in the SVG with the brand color
  const coloredSvg = FILESTACK_ICON_SVG.replace(
    /fill="#bdbdbd"/gi,
    `fill="${brandColor}"`
  )

  // Convert to data URI (URL encode the SVG content)
  const encodedSvg = encodeURIComponent(coloredSvg)
  return `url("data:image/svg+xml,${encodedSvg}")`
}
